import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ActressWorksList = ({ videos, className = "" }) => {
  const [sortBy, setSortBy] = useState('releaseDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterMaker, setFilterMaker] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  if (!videos || !videos.items || videos.items.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h3 className="text-xl font-bold mb-4">出演作品</h3>
        <p className="text-gray-500">出演作品がありません</p>
      </div>
    );
  }

  // Get unique makers for filter
  const makers = [...new Set(videos.items.map(video => video.maker).filter(Boolean))];

  // Apply filters and sorting
  let filteredVideos = [...videos.items];
  
  if (filterMaker) {
    filteredVideos = filteredVideos.filter(video => video.maker === filterMaker);
  }

  filteredVideos.sort((a, b) => {
    const aValue = a[sortBy] || '';
    const bValue = b[sortBy] || '';
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  // Pagination
  const totalPages = Math.ceil(filteredVideos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVideos = filteredVideos.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP');
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h3 className="text-xl font-bold mb-4 md:mb-0">
          出演作品 ({videos.total || filteredVideos.length})
        </h3>
        
        <div className="flex flex-col md:flex-row gap-2">
          {/* Maker Filter */}
          {makers.length > 1 && (
            <select
              value={filterMaker}
              onChange={(e) => {
                setFilterMaker(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">全メーカー</option>
              {makers.map(maker => (
                <option key={maker} value={maker}>{maker}</option>
              ))}
            </select>
          )}
          
          {/* Sort Controls */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="releaseDate">リリース日</option>
            <option value="title">タイトル</option>
            <option value="maker">メーカー</option>
            <option value="productCode">作品コード</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            {sortOrder === 'asc' ? '昇順 ↑' : '降順 ↓'}
          </button>
        </div>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {paginatedVideos.map(video => (
          <Link 
            to={`/videos/${video.id}`} 
            key={video.id}
            className="block border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {video.coverImage ? (
              <img 
                src={video.coverImage} 
                alt={video.title} 
                className="w-full h-48 object-cover"
              />
            ) : (
              <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500 text-sm">No Image</span>
              </div>
            )}
            
            <div className="p-3">
              <h4 className="text-sm font-medium line-clamp-2 mb-2">{video.title}</h4>
              <p className="text-xs text-gray-600 mb-1">{video.productCode}</p>
              {video.maker && (
                <p className="text-xs text-gray-600 mb-1">{video.maker}</p>
              )}
              {video.releaseDate && (
                <p className="text-xs text-gray-600">
                  {formatDate(video.releaseDate)}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            前へ
          </button>
          
          <span className="px-3 py-2 text-sm">
            {currentPage} / {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            次へ
          </button>
        </div>
      )}

      {filteredVideos.length === 0 && filterMaker && (
        <div className="text-center py-8">
          <p className="text-gray-500">選択したメーカーの作品がありません</p>
          <button
            onClick={() => setFilterMaker('')}
            className="mt-2 text-blue-600 hover:underline text-sm"
          >
            フィルターをクリア
          </button>
        </div>
      )}
    </div>
  );
};

export default ActressWorksList;